import type { Route } from "nextjs-routes";

import config from "configs/app";

// SEO 优化的 title 模板 - 保持在 60 字符以内，关键词在前
const TEMPLATE_MAP: Record<Route["pathname"], string> = {
  "/": "%network_name% Explorer | Blockchain Analytics & Network Stats",
  "/txs": "%network_name% Transactions | Real-time TX Explorer",
  "/internal-txs": "%network_name% Internal Transactions | Contract Calls",
  "/txs/kettle/[hash]": "%network_name% Kettle %hash% | Transaction List",
  "/tx/[hash]": "TX %hash% | %network_name% Transaction Details",
  "/blocks": "%network_name% Blocks | Latest Block Explorer",
  "/block/[height_or_hash]":
    "Block %height_or_hash% | %network_name% Block Details",
  "/block/countdown": "%network_name% Block Countdown | Target Block Timer",
  "/block/countdown/[height]": "Block %height% Countdown | %network_name%",
  "/accounts": "%network_name% Top Accounts | Wallet Rankings",
  "/accounts/label/[slug]": "%network_name% Addresses by Label | %slug%",
  "/address/[hash]": "Address %hash% | %network_name% Wallet Details",
  "/verified-contracts":
    "%network_name% Verified Contracts | Smart Contract List",
  "/contract-verification":
    "%network_name% Contract Verification | Verify Source",
  "/address/[hash]/contract-verification":
    "Verify Contract %hash% | %network_name%",
  "/tokens": "%network_name% Tokens | ERC-20 & NFT Token List",
  "/token/[hash]": "%network_name% Token Details | Analytics & Holders",
  "/token/[hash]/instance/[id]": "%network_name% NFT #%id% | Token Instance",
  "/apps": "%network_name% DApps | Explore Decentralized Apps",
  "/apps/[id]": "%network_name% DApp | Application Details",
  "/stats": "%network_name% Stats | Network Analytics & Charts",
  "/stats/[id]": "%network_name% %id% Chart | Network Statistics",
  "/api-docs": "%network_name% API Docs | Developer Documentation",
  "/search-results": "Search Results for %q% | %network_name%",
  "/auth/profile": "My Profile | %network_name% Account",
  "/account/merits": "My Merits | %network_name% Rewards",
  "/account/watchlist": "Watchlist | %network_name% Address Tracking",
  "/account/api-key": "API Keys | %network_name% Developer Access",
  "/account/custom-abi": "Custom ABI | %network_name% Contract Interface",
  "/account/tag-address": "Private Tags | %network_name% Address Labels",
  "/account/verified-addresses": "My Verified Addresses | %network_name%",
  "/public-tags/submit": "Submit Public Tag | %network_name%",
  "/withdrawals": "%network_name% Withdrawals | L2 to L1 Tracker",
  "/txn-withdrawals": "%network_name% L2 to L1 Message Relayer",
  "/visualize/sol2uml": "%network_name% Solidity UML | Contract Diagram",
  "/csv-export": "%network_name% CSV Export | Data Download",
  "/deposits": "%network_name% Deposits | L1 to L2 Tracker",
  "/output-roots": "%network_name% Output Roots | L2 State Commits",
  "/dispute-games": "%network_name% Dispute Games | Fraud Proofs",
  "/batches": "%network_name% Transaction Batches | L2 Batches",
  "/batches/[number]": "Batch %number% | %network_name% TX Batch Details",
  "/batches/celestia/[height]/[commitment]":
    "%network_name% Celestia Batch | DA Layer",
  "/blobs/[hash]": "Blob %hash% | %network_name% EIP-4844 Data",
  "/ops": "%network_name% User Operations | Account Abstraction",
  "/op/[hash]": "UserOp %hash% | %network_name% AA Transaction",
  "/404": "Page Not Found | %network_name% Explorer",
  "/name-domains": "%network_name% Domains | ENS Name Service",
  "/name-domains/[name]": "%name% Domain | %network_name% ENS Details",
  "/validators": "%network_name% Validators | Network Consensus",
  "/validators/[id]": "Validator %id% | %network_name% Node Details",
  "/epochs": "%network_name% Epochs | Consensus Periods",
  "/epochs/[number]": "Epoch %number% | %network_name% Period Details",
  "/gas-tracker": "%network_name% Gas Tracker | Live Fee Estimates",
  "/mud-worlds": "%network_name% MUD Worlds | On-chain Game Worlds",
  "/token-transfers": "%network_name% Token Transfers | ERC-20 Activity",
  "/advanced-filter": "%network_name% Advanced Filter | TX Search",
  "/pools": "%network_name% DEX Pools | Liquidity Analytics",
  "/pools/[hash]": "%network_name% Pool Details | DEX Liquidity",
  "/interop-messages": "%network_name% Interop Messages | Cross-chain",
  "/operations": "%network_name% Operations | Network Activity",
  "/operation/[id]": "Operation %id% | %network_name% Details",
  "/references": "%network_name% References | Reference Key Explorer",
  "/reference/[reference]": "Reference %reference% | %network_name% TX List",

  // multichain routes
  "/chain/[chain-slug]/accounts/label/[slug]":
    "%network_name% Addresses | Label Search",
  "/chain/[chain-slug]/advanced-filter":
    "%network_name% Advanced Filter | TX Search",
  "/chain/[chain-slug]/block/[height_or_hash]":
    "Block %height_or_hash% | %network_name%",
  "/chain/[chain-slug]/block/countdown": "%network_name% Block Countdown",
  "/chain/[chain-slug]/block/countdown/[height]":
    "Block %height% Countdown | %network_name%",
  "/chain/[chain-slug]/csv-export": "%network_name% CSV Export | Data Download",
  "/chain/[chain-slug]/op/[hash]": "UserOp %hash% | %network_name%",
  "/chain/[chain-slug]/token/[hash]": "%network_name% Token | Analytics",
  "/chain/[chain-slug]/token/[hash]/instance/[id]": "%network_name% NFT #%id%",
  "/chain/[chain-slug]/tx/[hash]": "TX %hash% | %network_name%",

  "/latest-l1-l2": "%network_name% L1→L2 Deposits | Bridge Tracker",
  "/latest-l2-l1": "%network_name% L2→L1 Withdrawals | Bridge Tracker",

  // service routes
  "/login": "%network_name% Login",
  "/sprite": "%network_name% SVG Sprite",
  "/chakra": "%network_name% Chakra UI",
  "/api/metrics": "%network_name% API Metrics",
  "/api/monitoring/invalid-api-schema": "%network_name% API Monitor",
  "/api/log": "%network_name% API Log",
  "/api/media-type": "%network_name% API Media",
  "/api/csrf": "%network_name% CSRF Token",
  "/api/healthz": "%network_name% Health Check",
  "/api/config": "%network_name% Config",
  "/api/sprite": "%network_name% API Sprite",
};

const TEMPLATE_MAP_ENHANCED: Partial<Record<Route["pathname"], string>> = {
  "/token/[hash]": "%network_name% %symbol_or_name% token details",
  "/token/[hash]/instance/[id]":
    "%network_name% token instance for %symbol_or_name%",
  "/apps/[id]": "%network_name% - %app_name%",
  "/address/[hash]": "%network_name% address details for %domain_name%",
  "/stats/[id]": "%title% chart on %network_name%",
};

export function make(pathname: Route["pathname"], isEnriched = false) {
  const template =
    (isEnriched ? TEMPLATE_MAP_ENHANCED[pathname] : undefined) ??
    TEMPLATE_MAP[pathname];
  const postfix = config.meta.promoteBlockscoutInTitle ? " | Blockscout" : "";

  return (template + postfix).trim();
}
